import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CeilingGauge — how much this agency can sign alone, and which buys went over.
 *
 * The Purchasing Authority card says "Tier 3 · $8M" as text. True, and inert.
 * Plotting their actual purchase orders against that line turns it into
 * something a rep can use: the POs above it are the ones where DGS became the
 * real buyer, which means a second set of relationships and a longer clock.
 *
 * Values are per PURCHASE ORDER, never per line — the delegated ceiling is a
 * per-transaction limit, and a gauge fed line amounts would put every agency
 * comfortably under its ceiling and be confidently wrong about the only thing
 * it says. The endpoint behind it groups by purchase_document_num for exactly
 * this reason.
 *
 * Presentational: data via props, no fetching, colours from CSS variables.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
const defaultFormatValue = (v) => {
    if (v >= 1000000000)
        return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000)
        return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000)
        return `$${(v / 1000).toFixed(0)}K`;
    return `$${Math.round(v)}`;
};
/*
 * The box is MEASURED, not assumed.
 *
 * This drew into a fixed 420x150 viewBox with preserveAspectRatio="none",
 * which stretches each axis independently to fill whatever cell the rep sized.
 * Measured 2026-09-14: a circle of r=3.5 rendered 6x4 in a 431x256 widget
 * (squashed 0.58:1), 6.2x10.1 in a 430x526 one (stretched 1.64:1) and
 * 4.7x8.9 at phone width (1.89:1). A dot was never round, and the direction
 * of the distortion flipped with the tile — "the dots shouldn't get smashed"
 * (Ryan). preserveAspectRatio="none" is right for a sparkline, where only the
 * path matters, and wrong for marks whose SHAPE carries meaning.
 *
 * So the viewBox is now the measured pixel box, 1:1, and every length below
 * is a real pixel.
 */
/**
 * Smallest box change worth a re-render, in px.
 *
 * 2 rather than 1: the oscillation this guards against is a one-pixel flip
 * between two layouts that each imply the other, so a threshold of 1 would
 * still let `254 -> 255` through and keep the loop alive.
 */
const SETTLE_PX = 2;
const FALLBACK_W = 420;
const FALLBACK_H = 150;
// Room for the value ticks the chart never had. The plot encodes value on Y
// and offered no way to read it — one dashed line and nothing else.
const PAD_L = 46;
const PAD_R = 10;
const PAD_T = 22;
const PAD_B = 26;
export const CeilingGauge = ({ purchaseOrders, ceiling, ranked, sinceYear, noCeilingNote, height = 170, fill = false, onPick, formatValue = defaultFormatValue, }) => {
    const boxRef = useRef(null);
    const [box, setBox] = useState({ w: FALLBACK_W, h: FALLBACK_H });
    const measure = useCallback(() => {
        const el = boxRef.current;
        if (!el)
            return;
        const r = el.getBoundingClientRect();
        // An unmeasured box keeps the fallback rather than collapsing to zero.
        if (r.width <= 0 || r.height <= 0)
            return;
        const w = Math.round(r.width);
        const h = Math.round(r.height);
        // Return the SAME object when nothing moved, or React re-renders, which
        // re-measures, which sets a fresh object, forever. useLayoutEffect here
        // runs on every render by design (the box can change without the props
        // changing), so the bail-out has to come from identity, not from a
        // dependency array. Setting `{ w, h }` unconditionally is what threw
        // "Maximum update depth exceeded" the first time this ran.
        //
        // THE IDENTITY BAIL-OUT IS NOT ENOUGH ON ITS OWN, which is what threw it
        // again (Ryan, 2026-09-16: "Maximum update depth exceeded" in
        // commitHookEffectListMount — this layout effect). Equality only stops the
        // loop when the size SETTLES. If the box alternates between two values the
        // bail-out never fires, and each measurement legitimately reports a change:
        // 254, 255, 254, 255…
        //
        // That is a real possibility here because the viewBox is the measured box,
        // so the picture's aspect ratio is whatever we last measured — a classic
        // measurement feedback path. The structural half of the fix is in CSS
        // (.clg-plot > svg is absolutely positioned, so the box sizes the picture
        // and never the reverse). This is the other half: ignore changes too small
        // to see. A 1px difference moves nothing a reader can perceive on a gauge,
        // and refusing to act on it makes the oscillation impossible to sustain
        // whatever the layout around it does.
        setBox((prev) => (Math.abs(prev.w - w) < SETTLE_PX && Math.abs(prev.h - h) < SETTLE_PX
            ? prev
            : { w, h }));
    }, []);
    useLayoutEffect(measure);
    useEffect(() => {
        const el = boxRef.current;
        if (!el || typeof ResizeObserver === 'undefined')
            return undefined;
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [measure]);
    const W = box.w;
    const H = box.h;
    /*
     * THE DOTS WERE NOT REACHABLE, and both of the things a reader wanted from
     * them hung off that.
     *
     * The radius follows the spacing so 200 marks do not smear into each other,
     * which on a real board cell floors at 1.6 — a 3.2px target. Nothing is
     * clickable at 3.2px, and the tooltip was the browser's native <title>, so
     * it needed a full second of hover on that same 3.2px target and arrived
     * unstyled. Ryan: "I need a tool tip and ability to drill down into any of
     * these purchases." Both were WIRED already; neither could be hit.
     *
     * So the pointer talks to the PLOT, not to a mark: one overlay resolves the
     * nearest point within PICK_RADIUS. That is the standard fix for dense
     * scatters, and it beats enlarging invisible per-dot targets — 200 extra
     * nodes whose hit areas would overlap each other anyway, resolving by
     * document order rather than by distance.
     */
    const [hit, setHit] = useState(null);
    const svgRef = useRef(null);
    const PICK_RADIUS = 22;
    const { points, over, ceilingY, hasCeiling, ticks, dotR } = useMemo(() => {
        const pos = (purchaseOrders || []).filter((p) => Number(p.total) > 0);
        if (pos.length === 0) {
            return { points: [], over: 0, scaleMax: 0, ceilingY: 0, hasCeiling: false, ticks: [], dotR: 3.5 };
        }
        const cap = Number(ceiling) > 0 ? Number(ceiling) : null;
        const biggest = Math.max(...pos.map((p) => Number(p.total)));
        // The ceiling must always be ON the chart. If every PO is small, a scale
        // fitted to the data alone would push the line off the top and the gauge
        // would silently become a plain scatter with no reference at all.
        const top = cap ? Math.max(biggest, cap * 1.15) : biggest;
        // Log scale: PO totals span several orders of magnitude, and on a linear
        // axis the whole population collapses onto the baseline under one outlier.
        const lo = Math.log10(Math.max(Math.min(...pos.map((p) => Number(p.total))), 1));
        const hi = Math.log10(Math.max(top, 10));
        const span = hi - lo || 1;
        const y = (v) => PAD_T + (1 - (Math.log10(Math.max(v, 1)) - lo) / span) * (H - PAD_T - PAD_B);
        const sorted = [...pos].sort((a, b) => {
            const da = a.startDate || '';
            const db = b.startDate || '';
            return da === db ? Number(b.total) - Number(a.total) : da.localeCompare(db);
        });
        const stepX = (W - PAD_L - PAD_R) / Math.max(sorted.length - 1, 1);
        /*
         * Ticks, because Y is the whole message. Four stops on the log scale,
         * rounded to a power of ten so the labels read as money rather than as
         * whatever the data happened to span.
         */
        const tickVals = [];
        const loE = Math.floor(lo);
        const hiE = Math.ceil(hi);
        const stepE = Math.max(1, Math.round((hiE - loE) / 3));
        for (let e = loE; e <= hiE; e += stepE) {
            const v = Math.pow(10, e);
            if (v >= 1 && y(v) >= PAD_T - 2 && y(v) <= H - PAD_B + 2)
                tickVals.push(v);
        }
        /*
         * 200 marks across ~370px is a step of under 2px, so at r=3.5 every dot
         * overlapped its neighbours — 100 colliding pairs measured. The radius
         * now follows the spacing, floored where a dot stops being clickable.
         */
        const r = Math.max(1.6, Math.min(3.5, stepX * 0.9));
        return {
            ticks: tickVals.map((v) => ({ v, y: y(v) })),
            dotR: r,
            points: sorted.map((p, i) => ({
                po: p,
                x: PAD_L + i * stepX,
                y: y(Number(p.total)),
                over: cap != null && Number(p.total) > cap,
            })),
            over: cap != null ? pos.filter((p) => Number(p.total) > cap).length : 0,
            ceilingY: cap != null ? y(cap) : 0,
            hasCeiling: cap != null,
        };
    }, [purchaseOrders, ceiling, W, H]);
    /** Pointer position → the nearest mark, in the SVG's own units. */
    const nearest = useCallback((clientX, clientY) => {
        const el = svgRef.current;
        if (!el || points.length === 0)
            return null;
        const r = el.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0)
            return null;
        // viewBox is the measured box, so this scale is 1 unless the cell is
        // mid-resize — but reading it keeps the maths honest either way.
        const px = ((clientX - r.left) / r.width) * W;
        const py = ((clientY - r.top) / r.height) * H;
        let best = null;
        let bestD = PICK_RADIUS * PICK_RADIUS;
        points.forEach((p, i) => {
            const dx = p.x - px;
            const dy = p.y - py;
            const d = dx * dx + dy * dy;
            if (d <= bestD) {
                bestD = d;
                best = i;
            }
        });
        return best;
    }, [points, W, H]);
    // A stale index outlives the data it pointed at — re-rank the POs or resize
    // the cell and `hit` would read a different purchase order, or none.
    useEffect(() => { setHit(null); }, [points]);
    if (points.length === 0) {
        return (_jsx("div", { className: `chs__empty${fill ? ' chs__empty--fill' : ''}`, style: fill ? undefined : { '--h': `${height}px` }, children: "No purchase orders recorded for this agency." }));
    }
    const total = points.length;
    return (_jsxs("div", { className: `clg${fill ? ' chfill' : ''}`, children: [_jsxs("div", { ref: boxRef, className: `clg-plot${fill ? ' clg-plot--fill' : ''}`, style: fill ? undefined : { '--h': `${height}px` }, children: [_jsxs("svg", { ref: svgRef, viewBox: `0 0 ${W} ${H}`, width: "100%", height: "100%", role: "img", "aria-label": hasCeiling
                            ? `${over} of the ${total} largest purchase orders exceeded the delegated ceiling`
                            : `${total} purchase orders by value`, className: `chsvg${fill ? ' chsvg--fill' : ''}`, children: [ticks.map((t) => (_jsxs("g", { children: [_jsx("line", { x1: PAD_L - 6, x2: W - PAD_R, y1: t.y, y2: t.y, stroke: "var(--color-border, #e4e4e7)", strokeWidth: 1, vectorEffect: "non-scaling-stroke" }), _jsx("text", { x: PAD_L - 10, y: t.y + 3.5, textAnchor: "end", fontSize: 10, fill: "var(--color-text-muted, #71717a)", children: formatValue(t.v) })] }, t.v))), hasCeiling && (_jsxs(_Fragment, { children: [_jsx("rect", { x: 0, y: 0, width: W, height: Math.max(ceilingY, 0), fill: "var(--color-danger, var(--color-error, #dc2626))", fillOpacity: 0.06 }), _jsx("line", { x1: PAD_L - 6, x2: W - PAD_R, y1: ceilingY, y2: ceilingY, stroke: "var(--color-danger, var(--color-error, #dc2626))", strokeWidth: 1.5, strokeDasharray: "5 4", vectorEffect: "non-scaling-stroke" }), _jsx("text", { x: W - PAD_R, y: Math.max(ceilingY - 5, 9), textAnchor: "end", fontSize: 10, fontWeight: 600, fill: "var(--color-danger, var(--color-error, #dc2626))", children: `ceiling ${formatValue(Number(ceiling))}` })] })), points.map((p, i) => (_jsx("circle", { cx: p.x, cy: p.y, r: p.over ? dotR * 1.3 : dotR, fill: p.over
                                    ? 'var(--color-danger, var(--color-error, #dc2626))'
                                    : 'var(--accent, var(--color-primary, #3b82f6))', fillOpacity: p.over ? 0.95 : 0.42, 
                                /* pointer-events live on the overlay below, so a 3.2px mark never
                                   has to be hit. The mark stays FOCUSABLE for the keyboard. */
                                className: `clg-dot${onPick ? ' chpick' : ''}`, tabIndex: onPick ? 0 : undefined, role: onPick ? 'button' : undefined, onFocus: () => setHit(i), onBlur: () => setHit((h) => (h === i ? null : h)), onKeyDown: onPick ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onPick(p.po);
                                    }
                                } : undefined, children: _jsxs("title", { children: [`PO ${p.po.documentNumber}`, p.po.supplier ? ` — ${p.po.supplier}` : '', ` · ${formatValue(Number(p.po.total))}`, p.po.startDate ? ` · ${p.po.startDate}` : '', p.over ? ' · over the ceiling, DGS signed' : ''] }) }, `${p.po.documentNumber}-${i}`))), hit != null && points[hit] && (_jsx("circle", { className: "clg-dot clg-dot--hit", cx: points[hit].x, cy: points[hit].y, r: Math.max(dotR, 3) + 3.5, fill: "none", stroke: points[hit].over
                                    ? 'var(--color-danger, var(--color-error, #dc2626))'
                                    : 'var(--accent, var(--color-primary, #3b82f6))', strokeWidth: 1.5, vectorEffect: "non-scaling-stroke" })), _jsx("rect", { className: `clg-hit${onPick ? ' chpick' : ''}`, x: 0, y: 0, width: W, height: H, fill: "transparent", onPointerMove: (e) => setHit(nearest(e.clientX, e.clientY)), onPointerLeave: () => setHit(null), onClick: onPick ? (e) => {
                                    const i = nearest(e.clientX, e.clientY);
                                    if (i != null)
                                        onPick(points[i].po);
                                } : undefined })] }), hit != null && points[hit] && (_jsxs("div", { 
                        /* The tooltip FOLLOWS the pointer across, and PINS to the top or
                           bottom edge of the plot. Floating it just above the mark is the
                           obvious design and it clipped: the card hides its overflow, the
                           box is ~124px tall, and the marks worth reading are the ones over
                           the ceiling — at the top, with nothing above them. Measured: a
                           mark at y=120 in a 255px plot put the first two lines 14px
                           outside. Pinning cannot clip at any mark, and it stops the box
                           jittering as the pointer crosses 200 dots two pixels apart.
                           It goes to whichever edge the mark is NOT near, so it never
                           covers the dot being read. */
                        className: `clg-tip${points[hit].x > W / 2 ? ' clg-tip--left' : ''}${points[hit].y < H / 2 ? ' clg-tip--low' : ''}`, 
                        /* Only the horizontal position is data. It travels as a custom
                           property — the one inline style this sheet's rule allows. */
                        style: { '--tip-x': `${(points[hit].x / W) * 100}%` }, children: [_jsx("span", { className: "clg-tip__val", children: formatValue(Number(points[hit].po.total)) }), points[hit].po.supplier && (_jsx("span", { className: "clg-tip__sup", children: points[hit].po.supplier })), _jsxs("span", { className: "clg-tip__meta", children: [`PO ${points[hit].po.documentNumber}`, points[hit].po.startDate ? ` · ${points[hit].po.startDate}` : ''] }), points[hit].over && (_jsx("span", { className: "clg-tip__over", children: "Over the ceiling \u2014 DGS signed this one" })), onPick && _jsx("span", { className: "clg-tip__cta", children: "Click to open the purchase order" })] }))] }), _jsx("p", { className: "clg-read", children: hasCeiling ? (_jsxs(_Fragment, { children: [_jsx("b", { className: over > 0 ? 'clg-over' : undefined, children: over }), ` of the ${total} largest ${over === 1 ? 'was' : 'were'} signed by DGS`, _jsx("span", { className: "clg-dim", children: ` · ceiling ${formatValue(Number(ceiling))}` }), ranked != null && ranked <= total && (_jsx("span", { className: "clg-dim", children: ` · top ${ranked} by value${sinceYear ? ` since ${sinceYear}` : ''}` }))] })) : (_jsx("span", { className: "clg-dim", children: `${total} purchase orders · ${noCeilingNote || 'no published ceiling for this entity'}` })) })] }));
};
export default CeilingGauge;
